// src/utils/cryptoUtils.js

/**
 * Cryptographic utility functions for BitDog Wallet
 * Uses Web Crypto API for secure encryption/decryption
 */

// Constants for encryption
const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const KEY_LENGTH = 256;
const ITERATIONS = 100000;
const HASH_ALGO = 'SHA-256';
const ENC_ALGO = 'AES-GCM';

// Keys for storing auto-generated password (encrypted)
const AUTO_PASSWORD_KEY = 'bitdog_auto_password';
const AUTO_PASSWORD_SALT_KEY = 'bitdog_auto_password_salt';
const AUTO_PASSWORD_IV_KEY = 'bitdog_auto_password_iv';

/**
 * Generate a cryptographic key from a password
 * @param {string} password - User password
 * @param {Uint8Array} salt - Salt for key derivation
 * @returns {Promise<CryptoKey>} - Derived key
 */
const deriveKey = async (password, salt) => {
  // Convert password to a key using PBKDF2
  const passwordBuffer = new TextEncoder().encode(password);
  const importedKey = await window.crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  // Derive a key for AES-GCM
  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: ITERATIONS,
      hash: HASH_ALGO
    },
    importedKey,
    { name: ENC_ALGO, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
};

/**
 * Encrypt data with a password
 * @param {string} data - Data to encrypt
 * @param {string} password - User password
 * @returns {Promise<string>} - Encrypted data as base64 string
 */
export const encrypt = async (data, password) => {
  if (!data || !password) {
    throw new Error('Data and password are required');
  }

  try {
    // Generate a random salt and IV
    const salt = window.crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    
    // Derive a key from the password
    const key = await deriveKey(password, salt);
    
    // Encrypt the data
    const dataBuffer = new TextEncoder().encode(data);
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      { name: ENC_ALGO, iv },
      key,
      dataBuffer
    );
    
    // Combine salt, IV, and encrypted data
    const result = new Uint8Array(salt.length + iv.length + encryptedBuffer.byteLength);
    result.set(salt, 0);
    result.set(iv, salt.length);
    result.set(new Uint8Array(encryptedBuffer), salt.length + iv.length);
    
    // Convert to base64 for storage
    return btoa(String.fromCharCode(...result));
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
};

/**
 * Decrypt data with a password
 * @param {string} encryptedData - Base64 encoded encrypted data
 * @param {string} password - User password
 * @returns {Promise<string>} - Decrypted data
 */
export const decrypt = async (encryptedData, password) => {
  if (!encryptedData || !password) {
    throw new Error('Encrypted data and password are required');
  }

  try {
    // Convert from base64
    const encryptedBytes = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
    
    // Extract salt and IV from the encrypted data
    const salt = encryptedBytes.slice(0, SALT_LENGTH);
    const iv = encryptedBytes.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
    const ciphertext = encryptedBytes.slice(SALT_LENGTH + IV_LENGTH);
    
    // Derive the key from the password
    const key = await deriveKey(password, salt);
    
    // Decrypt the data
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: ENC_ALGO, iv },
      key,
      ciphertext
    );
    
    // Convert the decrypted buffer to a string
    return new TextDecoder().decode(decryptedBuffer);
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error('Failed to decrypt data. Incorrect password or corrupted data.');
  }
};

/**
 * Generate a hash of a password for verification
 * @param {string} password - Password to hash
 * @param {Uint8Array} [salt] - Optional salt (generates new if not provided)
 * @returns {Promise<{hash: string, salt: string}>} - Hash and salt in base64
 */
export const hashPassword = async (password, providedSalt = null) => {
  try {
    const salt = providedSalt || window.crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    const passwordBuffer = new TextEncoder().encode(password);
    
    // Import the password as a key
    const importedKey = await window.crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    );
    
    // Derive bits using PBKDF2
    const derivedBits = await window.crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt,
        iterations: ITERATIONS,
        hash: HASH_ALGO
      },
      importedKey,
      256
    );
    
    // Convert to base64
    const hashBase64 = btoa(String.fromCharCode(...new Uint8Array(derivedBits)));
    const saltBase64 = btoa(String.fromCharCode(...salt));
    
    return { hash: hashBase64, salt: saltBase64 };
  } catch (error) {
    console.error('Password hashing error:', error);
    throw new Error('Failed to hash password');
  }
};

/**
 * Verify a password against a stored hash
 * @param {string} password - Password to verify
 * @param {string} storedHash - Previously stored hash (base64)
 * @param {string} storedSalt - Previously stored salt (base64)
 * @returns {Promise<boolean>} - True if password matches
 */
export const verifyPassword = async (password, storedHash, storedSalt) => {
  try {
    const salt = Uint8Array.from(atob(storedSalt), c => c.charCodeAt(0));
    const { hash } = await hashPassword(password, salt);
    return hash === storedHash;
  } catch (error) {
    console.error('Password verification error:', error);
    return false;
  }
};

/**
 * Check password strength (returns strength score 0-4)
 * @param {string} password - Password to check
 * @returns {number} - Strength score (0: very weak, 4: very strong)
 */
export const checkPasswordStrength = (password) => {
  if (!password) return 0;
  
  let strength = 0;
  
  // Length check
  if (password.length >= 8) strength += 1;
  if (password.length >= 12) strength += 1;
  
  // Character variety
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 1;
  if (/[0-9]/.test(password)) strength += 1;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 1;
  
  return Math.min(4, strength);
};

/**
 * Get feedback on password strength
 * @param {string} password - Password to check
 * @param {function} t - Translation function (from i18next)
 * @returns {{score: number, feedback: string, color: string}} - Feedback object
 */
export const getPasswordFeedback = (password, t) => {
  const score = checkPasswordStrength(password);
  
  let feedback;
  
  // Check if t is a valid function before using it
  if (typeof t === 'function') {
    try {
      // Try to use the translation function with the correct keys
      feedback = t(`auth.passwordStrength.${
        score === 0 ? 'veryWeak' : 
        score === 1 ? 'weak' : 
        score === 2 ? 'moderate' : 
        score === 3 ? 'strong' : 
        'veryStrong'
      }`);
    } catch (error) {
      // Fallback if translation fails
      feedback = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'][score] || 'Weak';
    }
  } else {
    // Fallback if no translation function is provided
    feedback = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'][score] || 'Weak';
  }
  
  const color = {
    0: '#ff4d4d', // Red
    1: '#ffa64d', // Orange
    2: '#ffff4d', // Yellow
    3: '#4da6ff', // Blue
    4: '#4dff4d'  // Green
  }[score];
  
  return { score, feedback, color };
};

/**
 * Generate a secure random password
 * @returns {string} - A cryptographically secure random password
 */
export const generateSecurePassword = () => {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+{}|:<>?';
  let password = '';
  const length = 24; // Increased for extra security
  
  // Use crypto.getRandomValues for secure randomness
  const values = new Uint32Array(length);
  window.crypto.getRandomValues(values);
  
  for (let i = 0; i < length; i++) {
    password += charset[values[i] % charset.length];
  }
  
  return password;
};

/**
 * Generate a secure device-specific secret
 * This generates a random value but stores it consistently for the device
 * @returns {Promise<Uint8Array>} - The device secret
 */
const getDeviceSecret = async () => {
  // Check if we already have a device secret
  const storedSecret = localStorage.getItem('bitdog_device_secret');
  
  if (storedSecret) {
    // Convert from base64 to Uint8Array
    return Uint8Array.from(atob(storedSecret), c => c.charCodeAt(0));
  } else {
    // Generate a new random secret for this device
    const newSecret = window.crypto.getRandomValues(new Uint8Array(32)); // 256 bits
    
    // Store it for future use (as base64)
    localStorage.setItem('bitdog_device_secret', btoa(String.fromCharCode(...newSecret)));
    
    return newSecret;
  }
};

/**
 * Derive a secure encryption key from device secret
 * @returns {Promise<CryptoKey>} - The derived key
 */
const deriveDeviceKey = async () => {
  try {
    // Get the device secret
    const deviceSecret = await getDeviceSecret();
    
    // Get or generate salt
    let saltBase64 = localStorage.getItem(AUTO_PASSWORD_SALT_KEY);
    let salt;
    
    if (!saltBase64) {
      salt = window.crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
      saltBase64 = btoa(String.fromCharCode(...salt));
      localStorage.setItem(AUTO_PASSWORD_SALT_KEY, saltBase64);
    } else {
      salt = Uint8Array.from(atob(saltBase64), c => c.charCodeAt(0));
    }
    
    // Import the device secret as a key
    const baseKey = await window.crypto.subtle.importKey(
      'raw',
      deviceSecret,
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );
    
    // Derive the encryption key
    return window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: ITERATIONS * 2, // Double iterations for more security
        hash: HASH_ALGO
      },
      baseKey,
      { name: ENC_ALGO, length: KEY_LENGTH },
      false,
      ['encrypt', 'decrypt']
    );
  } catch (error) {
    console.error('Failed to derive device key:', error);
    throw new Error('Device security initialization failed');
  }
};

/**
 * Encrypt data with the device-specific key
 * @param {string} data - Data to encrypt
 * @returns {Promise<string>} - Encrypted data as base64
 */
const encryptWithDeviceKey = async (data) => {
  try {
    // Get or generate IV
    let ivBase64 = localStorage.getItem(AUTO_PASSWORD_IV_KEY);
    let iv;
    
    if (!ivBase64) {
      iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
      ivBase64 = btoa(String.fromCharCode(...iv));
      localStorage.setItem(AUTO_PASSWORD_IV_KEY, ivBase64);
    } else {
      iv = Uint8Array.from(atob(ivBase64), c => c.charCodeAt(0));
    }
    
    // Get the device key
    const deviceKey = await deriveDeviceKey();
    
    // Encrypt the data
    const dataBuffer = new TextEncoder().encode(data);
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      { name: ENC_ALGO, iv },
      deviceKey,
      dataBuffer
    );
    
    // Return encrypted data as base64
    return btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer)));
  } catch (error) {
    console.error('Device encryption error:', error);
    throw new Error('Failed to encrypt with device key');
  }
};

/**
 * Decrypt data with the device-specific key
 * @param {string} encryptedData - Encrypted data as base64
 * @returns {Promise<string>} - Decrypted data
 */
const decryptWithDeviceKey = async (encryptedData) => {
  try {
    // Get the stored IV
    const ivBase64 = localStorage.getItem(AUTO_PASSWORD_IV_KEY);
    if (!ivBase64) {
      throw new Error('Device security data not found');
    }
    
    const iv = Uint8Array.from(atob(ivBase64), c => c.charCodeAt(0));
    
    // Get the device key
    const deviceKey = await deriveDeviceKey();
    
    // Decrypt the data
    const encryptedBuffer = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: ENC_ALGO, iv },
      deviceKey,
      encryptedBuffer
    );
    
    // Return decrypted data as string
    return new TextDecoder().decode(decryptedBuffer);
  } catch (error) {
    console.error('Device decryption error:', error);
    throw new Error('Failed to decrypt with device key');
  }
};

/**
 * Store the auto-generated password securely
 * UPDATED: Now properly encrypts the password instead of just encoding
 * @param {string} autoPassword - The auto-generated password
 * @param {string} [userPassword] - Optional user password for extra security
 */
export const storeAutoPassword = async (autoPassword, userPassword = null) => {
  try {
    if (userPassword) {
      // If user has a password, encrypt with it for maximum security
      const encrypted = await encrypt(autoPassword, userPassword);
      localStorage.setItem(AUTO_PASSWORD_KEY, 'encrypted:' + encrypted);
      console.log("Auto password stored with user password encryption");
    } else {
      // Otherwise use device-based encryption which is still better than Base64
      const deviceEncrypted = await encryptWithDeviceKey(autoPassword);
      localStorage.setItem(AUTO_PASSWORD_KEY, 'device:' + deviceEncrypted);
      console.log("Auto password stored with device-specific encryption");
    }
  } catch (error) {
    console.error('Failed to store auto-generated password:', error);
    throw new Error('Failed to secure wallet');
  }
};

/**
 * Retrieve the auto-generated password
 * UPDATED: Handles different encryption methods and migrates legacy formats
 * @param {string} [userPassword] - Optional user password for decryption
 * @returns {Promise<string>} - The auto-generated password
 */
export const getAutoPassword = async (userPassword = null) => {
  try {
    const storedValue = localStorage.getItem(AUTO_PASSWORD_KEY);
    
    if (!storedValue) {
      throw new Error('Auto-generated password not found');
    }
    
    // Handle different storage formats
    
    // 1. User password encrypted format (most secure)
    if (storedValue.startsWith('encrypted:')) {
      if (!userPassword) {
        throw new Error('User password required to decrypt auto-password');
      }
      
      const encrypted = storedValue.substring(10);
      return await decrypt(encrypted, userPassword);
    }
    
    // 2. Device-specific encrypted format (medium security)
    if (storedValue.startsWith('device:')) {
      const deviceEncrypted = storedValue.substring(7);
      return await decryptWithDeviceKey(deviceEncrypted);
    }
    
    // 3. Legacy v2 format (Base64 only - insecure)
    if (storedValue.startsWith('v2:')) {
      console.warn("Using legacy insecure auto-password format - will upgrade on next write");
      const password = atob(storedValue.substring(3));
      
      // Auto-migrate to more secure format
      try {
        await storeAutoPassword(password, userPassword);
        console.log("Auto-migrated auto-password to more secure format");
      } catch (migrationError) {
        console.warn("Failed to auto-migrate auto-password format:", migrationError);
      }
      
      return password;
    }
    
    // 4. Legacy plain format (Base64 only - insecure)
    if (storedValue.startsWith('plain:')) {
      console.warn("Using legacy insecure auto-password format - will upgrade on next write");
      const password = atob(storedValue.substring(6));
      
      // Auto-migrate to more secure format
      try {
        await storeAutoPassword(password, userPassword);
        console.log("Auto-migrated auto-password to more secure format");
      } catch (migrationError) {
        console.warn("Failed to auto-migrate auto-password format:", migrationError);
      }
      
      return password;
    }
    
    // 5. Try to handle any other legacy format
    try {
      if (userPassword) {
        return await decrypt(storedValue, userPassword);
      }
    } catch (e) {
      // Ignore and continue to fallback
    }
    
    // Last resort - try direct Base64 decode (very insecure)
    try {
      const password = atob(storedValue);
      // Auto-migrate to more secure format
      try {
        await storeAutoPassword(password, userPassword);
        console.log("Auto-migrated auto-password to more secure format");
      } catch (migrationError) {
        console.warn("Failed to auto-migrate auto-password format:", migrationError);
      }
      return password;
    } catch (e) {
      // If this fails too, we can't recover
      throw new Error('Unrecognized auto-password format');
    }
  } catch (error) {
    console.error('Failed to retrieve auto-generated password:', error);
    throw new Error('Failed to unlock wallet: ' + error.message);
  }
};